import React, { useState, useEffect } from "react";
import Login from "./Login";
import { PawIcon, ScissorsIcon, HotelIcon, HeartIcon } from "./Icons";

/* Inline wrench icon for Admin Panel */
const WrenchIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
);

/* Inline shop icon for Products */
const ShopIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

/* Inline help icon for FAQ */
const HelpIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/* Inline contact icon */
const ContactIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PUBLIC_SECTIONS = ["home", "services", "about"];

const Navbar = ({ activeTab, setActiveTab, user, setUser, handleLogout, openAdminLogin }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [visibleSection, setVisibleSection] = useState("home");

  // Scroll-tracked sections (only highlight via scroll when on the home page)
  const SCROLL_SECTIONS = ["home", "services", "about"];

  // Track which section is in viewport (only when no user)
  useEffect(() => {
    if (user) return;
    const observers = [];
    SCROLL_SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setVisibleSection(id);
        },
        { threshold: 0.25, rootMargin: "-70px 0px 0px 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [user]);

  // Smooth-scroll to a section (public) or switch tab (authenticated / special)
  const handleNavClick = (id) => {
    setMobileOpen(false);
    if (!user && SCROLL_SECTIONS.includes(id)) {
      // Clear activeTab so only scroll-position highlights
      setActiveTab("home");
      if (id === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          setTimeout(() => {
            const target = document.getElementById(id);
            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 120);
        }
      }
    } else {
      // Tab-based navigation: clear visibleSection so scroll can't override
      setVisibleSection("");
      setActiveTab(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Determine which item is "active" for nav highlight — only ONE at a time
  const getActive = (id) => {
    if (!user && SCROLL_SECTIONS.includes(id)) {
      // Only highlight scroll sections when no tab-based page is active
      if (activeTab && activeTab !== "home" && !SCROLL_SECTIONS.includes(activeTab)) return false;
      return visibleSection === id;
    }
    return activeTab === id;
  };

  const menuItems = [
    { id: "home", label: "Home", Icon: HotelIcon },
    { id: "services", label: "Services", Icon: ScissorsIcon },
    { id: "about", label: "About Us", Icon: HeartIcon },
    { id: "products", label: "Products", Icon: ShopIcon },
    { id: "faq", label: "FAQ", Icon: HelpIcon },
    { id: "contact", label: "Contact", Icon: ContactIcon },
  ];

  return (
    <>
      <nav style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(16px)", boxShadow: "0 4px 30px rgba(124,58,237,0.1), 0 1px 0 rgba(124,58,237,0.08)", position: "sticky", top: 0, left: 0, right: 0, width: "100%", margin: 0, zIndex: 50, borderBottom: "1px solid rgba(196,181,253,0.25)" }}>
        <div className="w-full px-4 md:px-8 py-3">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <button
              onClick={() => handleNavClick("home")}
              style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "none", border: "none", cursor: "pointer", transition: "transform 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.04)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              <div style={{ width: "2.8rem", height: "2.8rem", background: "linear-gradient(135deg, #7C3AED, #EC4899)", borderRadius: "0.9rem", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(124,58,237,0.35)" }}>
                <PawIcon size={20} color="white" strokeWidth={1.6} />
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "1.25rem", fontWeight: 900, background: "linear-gradient(90deg, #7C3AED, #EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                  Furpect Tails
                </div>
                <div style={{ fontSize: "0.65rem", color: "#9333EA", fontWeight: 700, letterSpacing: "0.04em" }}>PET GROOMING & HOTEL</div>
              </div>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
              {!user && menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-3.5 py-2.5 lg:px-4 lg:py-2.5 rounded-2xl font-semibold text-sm transition-all duration-300 hover:scale-105 flex items-center gap-1.5 ${
                    getActive(item.id)
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                      : "text-gray-700 hover:text-purple-600 hover:bg-purple-50"
                  }`}
                >
                  <item.Icon size={15} color="currentColor" />
                  <span>{item.label}</span>
                </button>
              ))}
              
              {user ? (
                <div className="flex items-center space-x-4 ml-4">
                  <div className="text-right hidden lg:block">
                    <p className="text-sm font-bold text-gray-800">{user.name}</p>
                    <p className="text-xs text-purple-600 capitalize">{user.role}</p>
                  </div>
                  {user.role !== "admin" && user.role !== "staff" && (
                    <button
                      onClick={() => setActiveTab("booking")}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-purple-500/50 hover:scale-105 transition-all"
                    >
                      Book Now
                    </button>
                  )}
                  {user.role !== "admin" && user.role !== "staff" && (
                    <button
                      onClick={() => setActiveTab("products")}
                      className={`px-6 py-3 rounded-2xl font-bold transition-all duration-300 flex items-center gap-2 ${
                        activeTab === "products"
                          ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                          : "text-gray-700 hover:text-purple-600 hover:bg-purple-50"
                      }`}
                    >
                      <ShopIcon size={16} color="currentColor" />
                      <span>Products</span>
                    </button>
                  )}
                  {user.role !== "admin" && (
                    <button
                      onClick={() => setActiveTab("dashboard")}
                      className={`px-6 py-3 rounded-2xl font-bold transition-all duration-300 flex items-center space-x-2 ${
                        activeTab === "dashboard"
                          ? "bg-purple-600 text-white shadow-lg"
                          : "text-purple-600 bg-purple-50 hover:bg-purple-100"
                      }`}
                    >
                      <PawIcon size={18} color="currentColor" />
                      <span>My Dashboard</span>
                    </button>
                  )}
                  {user.role === "admin" && (
                    <button
                      onClick={() => setActiveTab("admin")}
                      className={`px-6 py-3 rounded-2xl font-bold transition-all duration-300 flex items-center space-x-2 ${
                        activeTab === "admin"
                          ? "bg-purple-600 text-white shadow-lg"
                          : "text-purple-600 bg-purple-50 hover:bg-purple-100"
                      }`}
                    >
                      <WrenchIcon size={18} color="currentColor" />
                      <span>Admin Panel</span>
                    </button>
                  )}
                  <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className="px-6 py-3 bg-red-50 text-red-600 font-bold rounded-2xl border-2 border-red-100 hover:bg-red-100 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4 ml-4">
                  <button
                    onClick={() => setActiveTab("booking")}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-purple-500/50 hover:scale-105 transition-all"
                  >
                    Book Now
                  </button>
                  <button
                    onClick={() => setLoginOpen(true)}
                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-emerald-500/50 hover:scale-105 transition-all"
                  >
                    Log In
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-xl hover:bg-purple-100 transition-all"
            >
              <svg className={`w-6 h-6 transition-all ${mobileOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden bg-white/95 backdrop-blur-md border-t border-purple-100 px-6 py-4 space-y-2 overflow-y-auto transition-all duration-300 ${mobileOpen ? "max-h-[34rem] opacity-100" : "max-h-0 opacity-0"}`}>
          {!user && menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`w-full flex items-center p-4 rounded-2xl font-semibold transition-all ${
                getActive(item.id)
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                  : "text-gray-700 hover:bg-purple-50 hover:text-purple-600"
              }`}
            >
              <span className="flex items-center gap-2">
                <item.Icon size={16} color="currentColor" />
                <span>{item.label}</span>
              </span>
            </button>
          ))}
          {user ? (
            <>
              {user.role !== "admin" && (
                <button
                  onClick={() => {
                    setActiveTab("dashboard");
                    setMobileOpen(false);
                  }}
                  className={`w-full flex items-center p-4 rounded-2xl font-semibold transition-all ${
                    activeTab === "dashboard"
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                      : "text-purple-600 hover:bg-purple-50"
                  }`}
                >
                  <span className="flex items-center gap-2"><PawIcon size={16} color="currentColor" /> My Dashboard</span>
                </button>
              )}
              {user.role === "admin" && (
                <button
                  onClick={() => {
                    setActiveTab("admin");
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center p-4 rounded-2xl font-semibold bg-purple-50 text-purple-600"
                >
                  <span className="flex items-center gap-2"><WrenchIcon size={16} color="currentColor" /> Admin Panel</span>
                </button>
              )}
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="w-full text-left p-4 rounded-2xl font-bold text-red-500 hover:bg-red-50 transition-colors"
              >
                Logout ({user.name})
              </button>
            </>
          ) : (
            <div className="space-y-2 pt-2">
              <button
                onClick={() => {
                  setActiveTab("booking");
                  setMobileOpen(false);
                }}
                className="w-full p-4 rounded-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg flex items-center justify-center gap-2"
              >
                <span>Book Now</span>
              </button>
              <button
                onClick={() => {
                  setLoginOpen(true);
                  setMobileOpen(false);
                }}
                className="w-full p-4 rounded-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg flex items-center justify-center gap-2"
              >
                <span>Log In</span>
              </button>
            </div>
          )}
        </div>
      </nav>

      <Login 
        isOpen={loginOpen} 
        onClose={() => setLoginOpen(false)} 
        setUser={setUser}
        setActiveTab={setActiveTab}
        openAdminLogin={openAdminLogin}
      />

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in duration-200">
            <h2 className="text-2xl font-black text-gray-800 mb-2 mt-4">See you soon!</h2>
            <p className="text-gray-500 mb-8">Are you sure you want to log out of your account?</p>
            
            <div className="flex gap-4">
              <button 
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 px-4 rounded-2xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setShowLogoutConfirm(false);
                  setMobileOpen(false);
                  handleLogout();
                }}
                className="flex-1 py-3 px-4 rounded-2xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30 hover:-translate-y-1 transition-all"
              >
                Yes, Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
